import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import ProjectCard from './ProjectCard';
import './ProjectCarousel.css';

const ProjectCarousel = ({ projects, onProjectView, title, autoPlay = false, cardSize = "large" }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const carouselRef = useRef(null);

  const scrollCarousel = (direction) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    // Definir largura do card baseado no tamanho
    const cardWidth = cardSize === "small" ? 280 : 350;
    const gap = 30; // gap entre cards
    const scrollAmount = cardWidth + gap;
    
    const currentScroll = carousel.scrollLeft;
    const newScroll = direction === 'left' 
      ? currentScroll - scrollAmount 
      : currentScroll + scrollAmount;
    
    carousel.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });

    setScrollPosition(newScroll);
  };

  const canScrollLeft = () => {
    return scrollPosition > 0;
  };

  const canScrollRight = () => {
    const carousel = carouselRef.current;
    if (!carousel || !isInitialized) return false;
    
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    return scrollPosition < maxScroll - 10; // 10px de margem
  };

  const handleScroll = (e) => {
    setScrollPosition(e.target.scrollLeft);
  };

  // Inicializar o estado dos botões após o componente ser montado
  useEffect(() => {
    const initializeCarousel = () => {
      const carousel = carouselRef.current;
      if (carousel && projects && projects.length > 0) {
        // Aguardar um frame para garantir que o DOM esteja pronto
        requestAnimationFrame(() => {
          setScrollPosition(carousel.scrollLeft);
          setIsInitialized(true);
        });
      }
    };

    // Inicializar imediatamente
    initializeCarousel();

    // Também inicializar após um pequeno delay para garantir que imagens carregaram
    const timer = setTimeout(initializeCarousel, 100);

    return () => clearTimeout(timer);
  }, [projects]);

  // Reinicializar quando o tamanho da janela mudar
  useEffect(() => {
    const handleResize = () => {
      if (carouselRef.current && isInitialized) {
        setScrollPosition(carouselRef.current.scrollLeft);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isInitialized]);

  if (!projects || projects.length === 0) {
    return null;
  }

  const showNavigation = projects.length > 1;

  return (
    <div className="project-carousel">
      {title && (
        <motion.h3 
          className="carousel-title"
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3 }}
        >
          {title}
        </motion.h3>
      )}
      
      {showNavigation && (
        <div className="carousel-controls-container">
          <div className="carousel-controls">
            <button
              className={`carousel-btn carousel-btn--left ${!canScrollLeft() ? 'carousel-btn--disabled' : ''}`}
              onClick={() => scrollCarousel('left')}
              disabled={!canScrollLeft()}
            >
              <FiChevronLeft />
            </button>
            <button
              className={`carousel-btn carousel-btn--right ${!canScrollRight() ? 'carousel-btn--disabled' : ''}`}
              onClick={() => scrollCarousel('right')}
              disabled={!canScrollRight()}
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      )}
      
      <div className="projects-carousel-container">
        <div 
          className={`projects-carousel ${cardSize === 'small' ? 'projects-carousel--small' : ''}`}
          ref={carouselRef}
          onScroll={handleScroll}
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              className="project-wrapper"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <ProjectCard
                project={project}
                onView={() => onProjectView(project)}
                size={cardSize}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectCarousel;